import type {
  GetDefaultUserMentionsQuery,
  Scalars,
} from "@src/__generated__/graphql";
import {
  MentionSuggestionsList,
  type MentionSuggestionsListRef,
} from "@src/components/ui-kit/TextEditor/extensions/MentionSuggestionsList";
import { appStore } from "@src/stores/AppStore";
import type { UserType } from "@src/stores/models/Me";
import { useStore } from "@src/utils/hooks";
import type { Editor } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import tippy, { type Instance } from "tippy.js";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mention: {
      getUserMentionIds: () => string[];
      getTeamMentionIds: () => string[];
      getAssigneesMention: () => boolean;
    };
  }
}

const ASSIGNEES_MENTION_ID = "task";
const ASSIGNEES_MENTION_NAME = "Task";
const ASSIGNEES_MENTION_PREFIX = "assignees-";
const USER_MENTION_PREFIX = "user-";
const TEAM_MENTION_PREFIX = "team-";

type Suggestion = GetDefaultUserMentionsQuery["userSimpleMap"][0];

export const MentionExtension = (
  allowedMentions: UserType[],
  taskId?: Scalars["ID"]["input"],
) => {
  const { authStore } = useStore();

  return Mention.extend({
    // @ts-expect-error
    addCommands() {
      return {
        getUserMentionIds: () => () =>
          getMentionIds(this.editor, USER_MENTION_PREFIX),
        getTeamMentionIds: () => () =>
          getMentionIds(this.editor, TEAM_MENTION_PREFIX),
        getAssigneesMention: () => () =>
          getMentionIds(this.editor, ASSIGNEES_MENTION_PREFIX).length > 0,
      };
    },
    addAttributes() {
      return {
        ...this.parent?.(),
        user: {
          default: null,
          renderHTML: (attributes) => {
            if (
              authStore.user?.id !==
              attributes.id
                .replace(USER_MENTION_PREFIX, "")
                .replace(TEAM_MENTION_PREFIX, "")
                .replace(ASSIGNEES_MENTION_PREFIX, "")
            ) {
              return {};
            }
            return { class: "my-mention" };
          },
        },
      };
    },
  }).configure({
    HTMLAttributes: {
      class: "mention",
    },
    renderHTML({ options, node }) {
      const attrId = node.attrs.id
        .replace(USER_MENTION_PREFIX, "")
        .replace(TEAM_MENTION_PREFIX, "")
        .replace(ASSIGNEES_MENTION_PREFIX, "");

      return [
        "span",
        {
          "data-id": node.attrs.id,
          "data-type": "mention",
          "data-label": node.attrs.label,
          class:
            // eslint-disable-next-line lingui/no-unlocalized-strings
            authStore.user?.id === attrId ? "mention my-mention" : "mention",
          contenteditable: "false",
        },
        `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`,
      ];
    },
    suggestion: {
      // TODO allow space bugfix https://github.com/ueberdosis/tiptap/issues/214#issuecomment-964557956
      allowSpaces: true,
      items: async ({ query }) => {
        const suggestions: Suggestion[] = [];
        const teams = appStore.workspaceStore.teams;

        await appStore.workspaceStore.getDefaultMentionsUsers(
          allowedMentions,
          taskId,
        );

        // NOTE: Backwards compability for users used to use _ instead of space
        const normalizedQuery = appStore.workspaceStore.normalizeForSearch(
          query.replaceAll("_", " "),
        );

        // Regexp for task mention
        const regexpForTask = new RegExp(/^ta?s?k?$/g);
        if (query.length < 1 || !!normalizedQuery.match(regexpForTask)) {
          suggestions.push({
            id: ASSIGNEES_MENTION_PREFIX + ASSIGNEES_MENTION_ID,
            full_name: ASSIGNEES_MENTION_NAME,
          });
        }

        // Filter users from cached data
        const filteredUsers = appStore.workspaceStore.filterMentionsUsers(
          query,
          allowedMentions,
          taskId,
        );

        const userCount = filteredUsers.length;
        for (let i = 0; i < userCount; i++) {
          const user = filteredUsers[i];
          suggestions.push({
            id: USER_MENTION_PREFIX + user.id,
            full_name: user.full_name,
            image: user.image,
          });
        }

        // Filter teams
        if (appStore.authStore.isInternalUser) {
          const teamCount = teams.length;
          for (let i = 0; i < teamCount; i++) {
            const team = teams[i];
            if (
              !normalizedQuery ||
              appStore.workspaceStore
                .normalizeForSearch(team.name)
                .includes(normalizedQuery)
            ) {
              suggestions.push({
                id: TEAM_MENTION_PREFIX + team.id,
                full_name: team.name,
              });
            }
          }
        }

        return suggestions;
      },

      render: suggestionsRenderer,
    },
  });
};

function getMentionIds(editor: Editor, prefix: string): string[] {
  const mentionIDs: string[] = [];
  const parser = new DOMParser();
  const htmlString = editor.getHTML();
  const htmlDocument = parser.parseFromString(htmlString, "text/html");
  const mentions = htmlDocument.querySelectorAll(".mention");

  mentions.forEach((mentionTag) => {
    const mentionID = mentionTag.getAttribute("data-id");
    if (mentionID?.includes(prefix)) {
      mentionIDs.push(mentionID.replace(prefix, ""));
    }
  });

  return mentionIDs;
}

const suggestionsRenderer: SuggestionOptions["render"] = () => {
  let reactRenderer: ReactRenderer<MentionSuggestionsListRef>;
  let popup: Instance[] | undefined;

  return {
    onStart: (props) => {
      reactRenderer = new ReactRenderer(MentionSuggestionsList, {
        props,
        editor: props.editor,
      });

      // @ts-expect-error
      popup = tippy("body", {
        getReferenceClientRect: props.clientRect,
        appendTo: () => document.body,
        content: reactRenderer.element,
        showOnCreate: true,
        interactive: true,
        trigger: "manual",
        placement: "bottom-start",
        maxWidth: "500px",
        arrow: false,
        theme: "unstyled",
      });
    },
    onUpdate(props) {
      reactRenderer?.updateProps(props);

      popup?.forEach((tippy) => {
        tippy.setProps({
          // @ts-expect-error
          getReferenceClientRect: props.clientRect,
        });
      });
    },
    onKeyDown(props) {
      if (props.event.key === "Escape") {
        popup?.forEach((tippy) => {
          tippy.hide();
        });
        props.event.stopPropagation();

        return true;
      }

      if (!reactRenderer?.ref) {
        return false;
      }

      return reactRenderer.ref.onKeyDown(props);
    },

    onExit() {
      popup?.forEach((tippy) => {
        tippy.destroy();
      });
      reactRenderer?.destroy();
    },
  };
};
